import {
  CartItem,
  Portfolio,
  ProductLineAttr,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { PortfolioItemsByProductLine } from 'store/modules/proposal/types';
import { ProductLine } from 'types/enrollmentService';
import { deepCopy } from './CopyHelper';

/**
 * Keeps only the selected browsing criteria in a cart item.
 * Used when the proposal is no longer editable.
 *
 * @param cartItem - The original cart item
 * @returns A copy of the cart item with only the selected browsing criteria
 */
export function filterOutAllOtherBC(cartItem: CartItem): CartItem {
  const cartItemCopy = deepCopy(cartItem);
  const selectedBc = cartItemCopy.browsingCriteria[0];
  if (selectedBc) {
    cartItemCopy.browsingCriteria = { 0: selectedBc };
  }
  return cartItemCopy;
}

/**
 * Helper function to populate portfolio data grouped by product line.
 *
 * @param portfolios - List of portfolios
 * @param attributes - List of product line attributes
 * @param cartItems - List of cart items
 * @param filterOutNotSelectedBC - If true, keeps only the selected browsing criteria
 *                                 in each cart item (used when the proposal is not editable)
 * @returns Portfolio items grouped by product line
 */
export function populatePortfolioByProductLine(
  portfolios: Portfolio[],
  attributes: ProductLineAttr[],
  cartItems: CartItem[],
  filterOutNotSelectedBC: boolean = false,
): PortfolioItemsByProductLine[] {
  const portfolioItemsByProductLine: PortfolioItemsByProductLine[] = [];
  const productLineIdsInTheCart = [...new Set(cartItems.map((item) => item.productLineId))];

  productLineIdsInTheCart.forEach((productLineId) => {
    const portfolioIdsByLine = cartItems
      .filter((item) => item.productLineId === productLineId)
      .map((item) => item.portfolioId);

    const portfoliosByLine = portfolios
      .filter((portfolio) => portfolioIdsByLine.includes(portfolio.id))
      .map((portfolio) => {
        const cartItem = cartItems.find((item) => item.portfolioId === portfolio.id);
        if (cartItem) {
          return {
            portfolio,
            cartItem: filterOutNotSelectedBC ? filterOutAllOtherBC(cartItem) : cartItem,
          };
        }
        throw new Error(`Cart was corrupted and doesn't have cartItem with id ${portfolio.id}`);
      });

    portfolioItemsByProductLine.push({
      productLineId,
      portfolioToCartItems: portfoliosByLine,
      productLineAttributes: attributes.filter(
        (attr) => attr.productLine === PRODUCT_LINES_CONTENT_MAP[productLineId].joinedLabel,
      ),
    });
  });

  return portfolioItemsByProductLine;
}

export function getAttributeValues(
  productLine: ProductLine,
  attributeName: string,
  portfolioItemsByProductLines: PortfolioItemsByProductLine[],
): string[] {
  const productLineItem = portfolioItemsByProductLines.find((line) => line.productLineId === productLine.id);
  const productLineConfirmedItem = productLineItem?.portfolioToCartItems.find((item) => item.cartItem.confirmed);
  if (!productLineConfirmedItem) return [];

  return productLineConfirmedItem.portfolio.products
    .map(
      (product) =>
        product.bcDependentData[productLineConfirmedItem.cartItem.selectedIndex].productAttributes.find(
          (attr) => attr.attributeId === attributeName,
        )?.attributeValue,
    )
    .filter((value) => value !== undefined);
}
